import { useRef, useState, useEffect, type ChangeEvent, type KeyboardEvent, type FocusEvent } from "react";
import styles from "./DDSOtpInput.module.css";

export interface DDSOtpInputProps {
  value?: string;
  onChange?: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  inputMode?: "text" | "numeric" | "decimal" | "tel";
  pattern?: RegExp;
  placeholderChar?: string;
  className?: string;
  inputClassName?: string;
  "data-name"?: string;
  formError?: unknown;
  labeledBy?: string;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export const DDSOtpInput: React.FC<DDSOtpInputProps> = ({
  value: controlledValue,
  onChange,
  onComplete,
  length = 6,
  autoFocus = false,
  disabled = false,
  inputMode = "numeric",
  pattern = /^[0-9]*$/,
  placeholderChar = "",
  className,
  inputClassName,
  "data-name": dataName,
  formError,
  labeledBy,
  "aria-label": ariaLabel,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}) => {
  const [uncontrolledValue, setUncontrolledValue] = useState<string>("");
  const value = controlledValue !== undefined ? controlledValue : uncontrolledValue;
  const inputRefs = useRef<Array<HTMLInputElement | null>>(Array(length).fill(null));
  const isProgrammaticFocus = useRef(false);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      isProgrammaticFocus.current = true;
      inputRefs.current[0].focus();
      isProgrammaticFocus.current = false;
    }
  }, [autoFocus]);

  useEffect(() => {
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value, length, onComplete]);

  function findLeftMostEmptyIndex() {
    return value.split("").findIndex((char) => !char) === -1
      ? Math.min(value.length, length - 1)
      : value.split("").findIndex((char) => !char);
  }

  function handleInputChange(idx: number, e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const chars: string[] = raw.replace(/\s/g, "").split("");
    const newValue = value.split("");
    let changed = false;

    // Handle deletion case
    if (raw === "" && newValue[idx] !== "") {
      newValue[idx] = "";
      changed = true;
    } else {
      // Handle input case
      for (let i = 0; i < chars.length && idx + i < length; i++) {
        const char = chars[i];
        if (char && pattern.test(char)) {
          newValue[idx + i] = char;
          changed = true;
        }
      }
    }
    if (changed) {
      const joined = newValue.join("").slice(0, length);
      if (controlledValue === undefined) {
        setUncontrolledValue(joined);
      }
      onChange?.(joined);
      // Move focus
      const nextIdx = Math.min(idx + chars.length, length - 1);
      if (inputRefs.current[nextIdx]) {
        isProgrammaticFocus.current = true;
        inputRefs.current[nextIdx]?.focus();
        isProgrammaticFocus.current = false;
      }
    }
  }

  function handleKeyDown(idx: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newValue = value.split("");
      if (value[idx]) {
        // If there's a value in the current cell, clear it
        newValue[idx] = "";
      } else if (idx > 0) {
        // If the current cell is empty and we're not at the start, clear previous cell
        newValue[idx - 1] = "";
        isProgrammaticFocus.current = true;
        inputRefs.current[idx - 1]?.focus();
        isProgrammaticFocus.current = false;
      }
      const joined = newValue.join("");
      if (controlledValue === undefined) {
        setUncontrolledValue(joined);
      }
      onChange?.(joined);
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      isProgrammaticFocus.current = true;
      inputRefs.current[idx - 1]?.focus();
      isProgrammaticFocus.current = false;
      e.preventDefault();
    }
    if (e.key === "ArrowRight" && idx < length - 1) {
      const leftMostEmptyIdx = findLeftMostEmptyIndex();
      if (idx + 1 <= leftMostEmptyIdx) {
        isProgrammaticFocus.current = true;
        inputRefs.current[idx + 1]?.focus();
        isProgrammaticFocus.current = false;
        e.preventDefault();
      }
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text").replace(/\s/g, "");
    if (pattern.test(pasted)) {
      const joined = pasted.slice(0, length);
      if (controlledValue === undefined) {
        setUncontrolledValue(joined);
      }
      onChange?.(joined);
      // Focus last
      if (inputRefs.current[joined.length - 1]) {
        isProgrammaticFocus.current = true;
        inputRefs.current[joined.length - 1]?.focus();
        isProgrammaticFocus.current = false;
      }
      e.preventDefault();
    }
  }

  function handleFocus(idx: number, e: FocusEvent<HTMLInputElement>) {
    // Skip redirecting focus if it's programmatically set
    if (isProgrammaticFocus.current) {
      return;
    }

    const leftMostEmptyIdx = findLeftMostEmptyIndex();
    if (idx !== leftMostEmptyIdx) {
      e.preventDefault();
      isProgrammaticFocus.current = true;
      inputRefs.current[leftMostEmptyIdx]?.focus();
      isProgrammaticFocus.current = false;
    }
  }

  return (
    <div
      className={`${styles.otpInputRoot} ${className ?? ""}`}
      data-name={dataName}
      role="group"
      aria-label={ariaLabel}
    >
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => (inputRefs.current[idx] = el)}
          type="text"
          inputMode={inputMode}
          autoComplete="one-time-code"
          pattern={pattern.source}
          maxLength={1}
          className={`${styles.otpInputSlot} ${inputClassName ?? ""} ${value[idx] ? styles.otpInputSlotFilled : ""} ${
            formError ? styles.otpInputSlotError : ""
          }`}
          value={value[idx] ?? ""}
          onChange={(e) => handleInputChange(idx, e)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          onFocus={(e) => handleFocus(idx, e)}
          disabled={disabled}
          aria-label={`Digit ${idx + 1}`}
          aria-labelledby={idx === 0 ? labeledBy : undefined}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-disabled={disabled}
          placeholder={placeholderChar}
          data-name={dataName ? `${dataName}-slot-${idx}` : undefined}
        />
      ))}
    </div>
  );
};

DDSOtpInput.displayName = "DDSOtpInput";
